import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
// Types
import PageTypes from 'types/PageTypes';
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
import TimerTypes from 'types/TimerTypes';
import BannerTypes from 'types/BannerTypes';
// Models
import IPage from 'models/Page';

/**
 * Form-context-aware actions for the quiz `pages[]` array and its referenced
 * sub-entities (questions / banners / timers / waitings).
 *
 * Lives at the RHF layer so any consumer inside a `FormProvider` — the main
 * QuizFormPage sidebar AND the QuizAgentDialog previews — can call the same
 * implementation without re-deriving the same delete/cleanup branching.
 *
 * Implementation note: we use getValues + setValue instead of
 * useFieldArray.remove() so callers don't have to own (and pass in) the
 * `useFieldArray` instances themselves. The trade-off is that views which
 * ALSO mount a parallel `useFieldArray` on the same path will see their
 * auto-generated row ids regenerate after a delete. That matters for
 * QuizFormSidebar/PagesMenu, whose framer-motion reorder uses those ids —
 * so it's fine to leave PagesMenu's existing local handleDelete alone if
 * those animations regress. For QuizAgentChatScreen (no useFieldArray,
 * preview is rebuilt from the watched form values anyway) setValue is the
 * simpler path.
 */
export const useQuizPageActions = () => {
  const { getValues, setValue } = useFormContext<any>();

  /**
   * Delete a page by its UUID. Also removes the question / banner / timer /
   * waiting record(s) the page referenced — same cleanup as PagesMenu's
   * `handleDelete`, just expressed through setValue.
   */
  const deletePage = (uuid: string) => {
    const pages: IPage[] = getValues('pages') || [];
    const page = pages.find((p) => p.uuid === uuid);
    if (!page) return;

    const refsToDelete = new Set<string>(page.refs || []);

    // Map each PageTypes value to the form path of the array it references.
    // Both Question and AdditionalQuestions live under `questions`.
    const subEntityPath: Partial<Record<PageTypes, string>> = {
      [PageTypes.Question]: 'questions',
      [PageTypes.AdditionalQuestions]: 'questions',
      [PageTypes.Banner]: 'banners',
      [PageTypes.Timer]: 'timers',
      [PageTypes.Waiting]: 'waitings',
    };

    const path = subEntityPath[page.type];
    if (path) {
      const list: any[] = getValues(path) || [];
      const next = list.filter((entry) => !entry?.ref || !refsToDelete.has(entry.ref));
      if (next.length !== list.length) {
        setValue(path, next, { shouldDirty: true, shouldTouch: true });
      }
    }

    setValue(
      'pages',
      pages.filter((p) => p.uuid !== uuid),
      { shouldDirty: true, shouldTouch: true }
    );
  };

  /**
   * Add a new page at a specific position in the pages[] array.
   * Returns the uuid of the newly created page so callers can open the edit
   * dialog immediately after insertion.
   *
   * @param pageType   The type of page to create.
   * @param insertAtIndex  Position in pages[] to insert the new page before.
   *                       Pass pages.length (or a very large number) to append.
   * @param questionType   Only relevant when pageType is Question or
   *                       AdditionalQuestions (defaults to SingleChoice).
   */
  const addPageAt = (
    pageType: PageTypes,
    insertAtIndex: number,
    questionType: QuestionTypes = QuestionTypes.SingleChoice,
  ): string => {
    const quizType: QuizTypes = getValues('type');
    const isScoringQuiz = [
      QuizTypes.Scoring,
      QuizTypes.Prediction,
      QuizTypes.BeatTheExpert,
      QuizTypes.Streak,
    ].includes(quizType);
    const initialScore = quizType === QuizTypes.Prediction ? 3000 : 1000;

    const ref = uuidv4();
    const uuid = uuidv4();

    if (
      pageType === PageTypes.Question ||
      pageType === PageTypes.AdditionalQuestions
    ) {
      const question: any = {
        ref,
        title: '',
        description: '',
        type: questionType,
        image:
          pageType === PageTypes.Question
            ? 'https://media.quizwizards.com/question-default.jpg'
            : '',
        postAnswerImage: '',
        video: '',
        audio: '',
        correctAnswerDescription: '',
        answers: [],
        timeout:
          quizType === QuizTypes.Prediction ||
          [
            QuestionTypes.ConnectionGrid,
            QuestionTypes.ImagePin,
            QuestionTypes.TopList,
          ].includes(questionType)
            ? 30
            : 10,
      };
      if (isScoringQuiz && pageType === PageTypes.Question)
        question['score'] = initialScore;
      if (quizType === QuizTypes.Prediction) {
        question['scorePlaceholder1'] = 'Score 1';
        question['scorePlaceholder2'] = 'Score 2';
      }
      if (pageType === PageTypes.AdditionalQuestions)
        question['title'] = `Survey Question${isScoringQuiz ? ` - Won't affect score` : ''}`;
      if (questionType === QuestionTypes.ImagePin) {
        question['config'] = {
          image: 'https://media.quizwizards.com/image_map_default.png',
          x: 0.5,
          y: 0.5,
          tolerance: 0.08,
        };
      }

      const questions: any[] = getValues('questions') || [];
      setValue('questions', [...questions, question], {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (pageType === PageTypes.Banner) {
      const banner = {
        ref,
        type: BannerTypes.Image,
        source: '',
        title: '',
        image: '',
        text: '',
      };
      const banners: any[] = getValues('banners') || [];
      setValue('banners', [...banners, banner], {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (pageType === PageTypes.Timer) {
      const timer = {
        ref,
        type: TimerTypes.Absolute,
        time: '',
        title: '',
        url: '',
        image: '',
      };
      const timers: any[] = getValues('timers') || [];
      setValue('timers', [...timers, timer], {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (pageType === PageTypes.Waiting) {
      const waiting = { ref, eventId: '', image: '', text: '' };
      const waitings: any[] = getValues('waitings') || [];
      setValue('waitings', [...waitings, waiting], {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    const newPage: any = { uuid, refs: [ref], type: pageType };
    const pages: IPage[] = getValues('pages') || [];
    const clampedIndex = Math.min(insertAtIndex, pages.length);
    const nextPages = [
      ...pages.slice(0, clampedIndex),
      newPage,
      ...pages.slice(clampedIndex),
    ];
    setValue('pages', nextPages, { shouldDirty: true, shouldTouch: true });

    return uuid;
  };

  return { deletePage, addPageAt };
};
